import { Ellipsis } from '@zoom/components';
import { difference } from 'ramda';
import React, { useMemo } from 'react';
import { useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { ACDStatusLabel, composeReasonCodeUniqueKey, getDuplicatedReasonCodes } from './ACDStatusLabel';
import { CustomSelectionLabelProps, SelectField } from '../../components/Inputs/SelectField';
import { AdherenceEventType, AdherenceReasonCode, AdherenceSettingAcdStatus } from '../../graphql/components';
import { SortOrder } from '../../types';
import { recordBy } from '../../utils/array';
import { customSort } from '../../utils/sort';

type ACDStatusSelectFieldProps = {
  name: string;
  dataTestId?: string;
  eventTypes: AdherenceEventType[];
  reasonCodes: AdherenceReasonCode[];
  usedAcdStatuses: AdherenceSettingAcdStatus[];
} & Partial<Pick<React.ComponentProps<typeof SelectField>, 'className' | 'disabled' | 'label' | 'validate'>>;

export const createAcdStatusOptions = (eventTypes: AdherenceEventType[], reasonCodes: AdherenceReasonCode[]) =>
  eventTypes.reduce<{ id: string; name: string }[]>((options, event) => {
    if (event.reasonCodeRequired) {
      const variations = reasonCodes
        .filter((code) => code.eventType === event.eventType)
        .map((code) => ({
          id: `${event.id}-${code.id}`,
          name: `${event.eventName} - ${code.label} (${code.code})`,
        }));

      return [...options, ...variations];
    }

    const option = {
      id: event.id,
      name: event.eventName,
    };

    return [...options, option];
  }, []);

export const getAcdStatusIds = (status: string) => {
  const [eventTypeId, reasonCodeId] = status.split('-');

  return {
    agentEventTypeId: eventTypeId,
    agentEventReasonCodeId: reasonCodeId || null,
  };
};

export const composeAcdStatusId = (status: AdherenceSettingAcdStatus) => {
  const { agentEventTypeId, agentEventReasonCodeId } = status;

  const code = agentEventReasonCodeId !== null ? `-${agentEventReasonCodeId}` : '';

  return `${agentEventTypeId}${code}`;
};

export const ACDStatusSelectField: React.FC<ACDStatusSelectFieldProps> = ({
  className,
  dataTestId = 'acd-status-dropdown',
  disabled,
  label = 'scheduleAdherence.acdStatus',
  name,
  validate,
  eventTypes,
  reasonCodes,
  usedAcdStatuses,
}) => {
  const { meta } = useField<string[]>(name);
  const { t } = useTranslation();

  const sortedEvents = customSort(eventTypes, ['eventName'], SortOrder.ASCENDING);
  const sortedCodes = customSort(reasonCodes, ['label', 'code', 'sourceId'], SortOrder.ASCENDING);

  const allOptions = useMemo(() => createAcdStatusOptions(sortedEvents, sortedCodes), [sortedEvents, sortedCodes]);

  const selectedOptionIds = meta.initial ?? [];
  const usedOptionIds = usedAcdStatuses.map(composeAcdStatusId);
  const removeOptionIds = difference(usedOptionIds, selectedOptionIds);

  const options = allOptions.filter((option) => !removeOptionIds.includes(option.id));

  const optionsRecord = recordBy(options, 'id');
  const reasonCodesRecord = recordBy(reasonCodes, 'id');
  const eventTypesRecord = recordBy(eventTypes, 'id');

  const duplicatedReasonCodes = getDuplicatedReasonCodes(reasonCodes);

  const renderSelectionLabel = ({ value }: CustomSelectionLabelProps) => {
    if (value.length === 0) {
      return null;
    }

    let labelText = optionsRecord[value[0]].name;

    if (value.length > 1) {
      labelText = t('filter.selected', { count: value.length });
    }

    return (
      <div data-test-id="selected-value-label">
        {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
        <Ellipsis>{labelText}</Ellipsis>
      </div>
    );
  };

  return (
    <SelectField
      className={className}
      data-test-id={dataTestId}
      disabled={disabled}
      t={t}
      name={name}
      label={label}
      options={options}
      multiple
      customLabelComponent={({ value }) => {
        const { agentEventTypeId, agentEventReasonCodeId } = getAcdStatusIds(value);
        const reasonCode = agentEventReasonCodeId ? reasonCodesRecord[agentEventReasonCodeId] : undefined;

        return (
          <ACDStatusLabel
            eventType={eventTypesRecord[agentEventTypeId]}
            reasonCode={reasonCode}
            showSource={reasonCode ? duplicatedReasonCodes[composeReasonCodeUniqueKey(reasonCode)] : false}
          />
        );
      }}
      customSelectionLabelComponent={renderSelectionLabel}
      validate={validate}
    />
  );
};
